'use es6';

import get from 'hs-lodash/get';
import I18n from 'I18n';
import { flatten, findWhere, isEmpty, object, find, isEqual, isUndefined, uniq } from 'underscore';
import { createSelector } from 'reselect';
import createFormStyle from 'forms-utils-lib/formStyle/utils/createFormStyle';
import FormStyleKeys from 'forms-utils-lib/constants/formStyle/FormStyleKeys';
import FormStyleKeysToGlobalMap from 'forms-utils-lib/constants/formStyle/FormStyleKeysToGlobalMap';
import { findMetaDataKey } from 'FormsCommonComponents/form-definition/utils/MetaDataUtils';
import { DISABLE_COOKIE_SUBMISSION } from 'FormsCommonComponents/form-definition/constants/MetaDataKeys';
import { ACCOUNT_BUSINESS_UNIT_ID } from 'FormsCommonComponents/constants/BusinessUnits';
import { getIsExternalEditorCOS } from 'FormsUI/utils/QueryParams';
import { getGlobalFormStyles } from 'FormsUI/selectors/Settings';
import { hasEmailSendingFreeScope, hasFormsWriteAccess as hasFormsWriteAccessFromSelector } from 'FormsUI/selectors/Scope';
import { ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import { TabValidationsKeys } from 'FormsUI/constants/FormFieldValidations';
import { DefaultThemes, IS_LEGACY_THEME_ALLOWED } from 'FormsUI/constants/FormThemes';
import { getPropertyId, getPropertyObjectTypeId, updateMapKeysForUsingObjectTypes } from 'FormsUI/utils/PropertiesUtils';
import { isFieldAPaymentField } from 'FormsUI/utils/FormFieldsUtils';
import PublishState from 'FormsUI/constants/PublishState';
import { MetaDataProperties } from 'FormsUI/constants/FormMetaDataProperties';
import FormTypes from 'forms-utils-lib/constants/formDefinition/FormTypes';
import { requiredTicketPropertiesForConversations } from 'forms-utils-lib/constants/Properties';
import * as UpdateState from 'FormsUI/lib/UpdateState';
import { getDefaultLang } from 'forms-utils-lib/language/getDefaultLang';
import { getFormsLangToLang } from 'FormsUI/utils/Lang';
import { PropertyObjectTypesIds, PropertyObjectTypes } from 'ui-forms-components/constants/PropertyObjectTypes';
import { isContactProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import { isRichTextInternalIdEqualFieldGroupInternalId } from 'FormsUI/utils/RichText';
import getAllFieldsFromForm from 'FormsUI/utils/fields/getAllFieldsFromForm';
import isTicketField from 'forms-utils-lib/formField/utils/isTicketField';
import { PRIVACY_ADS_CONSENT_NAME } from 'FormsUI/constants/AdConsent';
import contactObjectTypeId from 'ui-forms-components/constants/objectTypeIds/contactObjectTypeId';
const reduceToKeyedObject = (memo, id) => Object.assign({}, memo, {
  [id]: true
});
const EMAIL_FIELD_NAME = 'email';
const selectObjectTypeIdToNameMap = state => state.customObjects.objectTypeIdsToNameMap;
export function getDefinition(state) {
  return state.formDefinition.definition || {};
}
export const getDefinitionOrNull = state => get(state, 'formDefinition.definition', null);
export const getLastFetched = state => state.formDefinition.lastFetched;
export const getPublishedForm = state => state.formDefinition.publishedForm;
export function getFormDefinitionRequestState(state) {
  return state.formDefinition.requestState;
}
export function getFormPublishState(state) {
  return state.formDefinition.formPublishState;
}
export const getFormInvalidProperty = state => state.formDefinition.invalidProperty || {
  objectTypeId: '',
  propertyName: ''
};
export const hasDraft = (state, {
  form
} = {}) => {
  return !isUndefined(form) ? !!form.hasDraft : getFormPublishState(state) === PublishState.PUBLISHED_WITH_DRAFT;
};
export function getHighlightAnimationMap(state) {
  return state.formDefinition.animationMap;
}
export function getFormFieldGroups(state) {
  return getDefinition(state).formFieldGroups;
}
export function getBusinessUnitId(state) {
  return getDefinition(state).businessUnitId || ACCOUNT_BUSINESS_UNIT_ID;
}
export function getFormGuid(state) {
  return getDefinition(state).guid;
}
export function getFormType(state) {
  return getDefinition(state).formType;
}
export function getIsTicketForm(state) {
  return getFormType(state) === FormTypes.TICKET;
}
export function getFormFieldGroup(state, formFieldGroupIndex) {
  return getFormFieldGroups(state)[formFieldGroupIndex];
}
export const getQueuedFormFieldGroups = createSelector(getFormFieldGroups, formFieldGroups => {
  return formFieldGroups.filter(formFieldGroup => !formFieldGroup.default);
});
export function getSubmitText(state) {
  return getDefinition(state).submitText;
}
export const isCaptchaEnabled = state => getDefinition(state).captchaEnabled;
export const isCaptchaConfigurable = state => hasEmailSendingFreeScope(state);
export const getFormFields = createSelector(getFormFieldGroups, formFieldGroups => {
  return flatten((formFieldGroups || []).map(el => el.fields));
});
const getFormFieldId = (state, props) => props.formFieldId;
export const getFormField = createSelector([getFormFields, getFormFieldId], (formFields, formFieldId) => {
  return find(formFields, field => getPropertyObjectTypeId(field) === formFieldId);
});
export const getAllFieldsByPropertyId = createSelector(getFormFieldGroups, getAllFieldsFromForm);
export const getFormFieldGroupForFormField = createSelector([getFormFieldGroups, getFormFieldId], (formFieldGroups, formFieldId) => {
  return find(formFieldGroups, formFieldGroup => {
    return find(formFieldGroup.fields, field => getPropertyId(field) === formFieldId);
  }) || {};
});
export const getFormFieldsByPropertyId = createSelector([getFormFields], formFields => formFields.reduce((memo, formField) => {
  const id = getPropertyObjectTypeId(formField);
  memo[id] = formField;
  return memo;
}, {}));
const getRichTextPrivateInternalId = (state, selectedFormFieldGroupIndex) => selectedFormFieldGroupIndex;
export const getRichText = createSelector([getFormFieldGroups, getRichTextPrivateInternalId], (formFieldGroups, richTextPrivateInternalId) => {
  const targetFormFieldGroup = find(formFieldGroups, formFieldGroup => {
    return isRichTextInternalIdEqualFieldGroupInternalId(formFieldGroup, richTextPrivateInternalId);
  });
  return targetFormFieldGroup ? targetFormFieldGroup.richText : {
    content: ''
  };
});
export const getFormFieldsWithDependentFields = createSelector(getFormFields, formFields => {
  return flatten(formFields).filter(formField => {
    const dependentFields = flatten((formField.dependentFieldFilters || []).map(el => el.dependentFormField));
    return !isEmpty(dependentFields);
  });
});
export function getAllUsedFormFields(state) {
  const formFieldsGroups = getFormFieldGroups(state) || [];
  const formFields = formFieldsGroups.map(formFieldGroup => formFieldGroup.fields);
  const formFieldsWithDependentFields = flatten(formFields).map(formField => {
    const dependentFields = flatten((formField.dependentFieldFilters || []).map(el => el.dependentFormField));
    if (isEmpty(dependentFields)) {
      return formField;
    }
    return [formField].concat(dependentFields);
  });
  return flatten(formFieldsWithDependentFields);
}

// used to get a form field from all fields including dependent fields
export const getFormFieldFromAllFields = createSelector([getAllUsedFormFields, getFormFieldId], (formFields, formFieldId) => {
  return find(formFields, field => getPropertyObjectTypeId(field) === formFieldId);
});
const getDependentFieldsFromFormFields = formFields => {
  const formFieldsWithDependentFields = flatten(formFields).map(formField => {
    const dependentFields = flatten((formField.dependentFieldFilters || []).map(el => el.dependentFormField));
    if (isEmpty(dependentFields)) {
      return formField;
    }
    return [formField].concat(dependentFields);
  });
  return flatten(formFieldsWithDependentFields);
};
export function getAllUsedFormFieldNames(state) {
  const fields = getAllUsedFormFields(state);
  return fields.map(formField => formField.name).reduce(reduceToKeyedObject, {});
}
export function getDependentFieldsOfField(state, {
  formFieldId
}) {
  const formField = getFormField(state, {
    formFieldId
  });
  if (formField) {
    return formField.dependentFieldFilters.map(dependentField => dependentField.dependentFormField);
  }
  return [];
}
export function getDependentFieldsOfAllFields(state) {
  const formFieldsWithDependentFields = getFormFieldsWithDependentFields(state);
  if (formFieldsWithDependentFields) {
    return flatten(formFieldsWithDependentFields.map(field => field.dependentFieldFilters.map(dependentField => dependentField.dependentFormField)));
  }
  return [];
}
export function getDependentFieldsOfAllTicketFields(state) {
  const formFieldsWithDependentFields = getFormFieldsWithDependentFields(state);
  const ticketFieldsWithDependentFields = formFieldsWithDependentFields.filter(field => isTicketField(field));
  if (ticketFieldsWithDependentFields) {
    return flatten(ticketFieldsWithDependentFields.map(ticketField => ticketField.dependentFieldFilters.map(dependentField => dependentField.dependentFormField)));
  }
  return [];
}
export function getAllQueuedFormFieldsWithDependentFields(state) {
  const queuedFields = getQueuedFormFieldGroups(state);
  return queuedFields.reduce((prevFields, {
    fields
  }) => [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  ...prevFields, ...getDependentFieldsFromFormFields(fields)], []);
}
export function getAllUsedFormFieldIds(state) {
  const fields = getAllUsedFormFields(state);
  return fields.map(getPropertyId).reduce(reduceToKeyedObject, {});
}
export function getAllUsedFormFieldObjectTypeId(state) {
  const allFields = getAllUsedFormFields(state);
  const fields = allFields.map(getPropertyObjectTypeId).reduce(reduceToKeyedObject, {});
  return fields;
}
export function getFormName(state) {
  return getDefinition(state).name;
}
export function getFormMetaData(state) {
  return getDefinition(state).metaData;
}
export function getFormCaptchaEnabled(state) {
  return getDefinition(state).captchaEnabled;
}
export function getIgnoreCurrentValues(state) {
  return getDefinition(state).ignoreCurrentValues;
}
export function getFormThemeName(state) {
  return getDefinition(state).themeName;
}
export const getEmbedVersion = state => getDefinition(state).embedVersion;
export function getThankYouMessageIfPresent(state) {
  const thankYouMessage = getDefinition(state).inlineMessage;
  return !isEmpty(thankYouMessage) ? thankYouMessage : undefined;
}
export function getRedirectUrlIfPresent(state) {
  const redirectUrl = getDefinition(state).redirect;
  return !isEmpty(redirectUrl) ? redirectUrl : undefined;
}
export function getPostSubmissionOption(state) {
  return state.formDefinition.postSubmissionOption;
}
export function getNotifyRecipients(state) {
  const notifyRecipients = getDefinition(state).notifyRecipients;
  return !isEmpty(notifyRecipients) ? notifyRecipients.split(',') : [];
}
export function getMetaDataValue(state, name) {
  const metaData = findWhere(getFormMetaData(state), {
    name
  });
  return metaData ? metaData.value : undefined;
}
export function getUpdatedAt(state) {
  return I18n.moment(getDefinition(state).updatedAt).utc().valueOf();
}
export function getFormDefinitionValidation(state) {
  return state.formDefinition.validation;
}
export function getFormDefinitionUpdateErrors(state) {
  return state.formDefinition.updateErrors.fields || {};
}
export function getFormDefinitionUpdateErrorList(state) {
  const updateErrors = getFormDefinitionUpdateErrors(state);
  const fieldErrorsKeys = Object.keys(updateErrors);
  const errorList = fieldErrorsKeys.reduce( /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  (output, fieldKey) => [...output, ...updateErrors[fieldKey]], []);
  return errorList;
}
export const getEditorValidation = createSelector(getFormDefinitionValidation, validationsByStep => {
  const validationKeys = Object.values(validationsByStep || {}).reduce((acc, stepValidation) => {
    const fieldValidations = Object.values(stepValidation || {});
    const keys = fieldValidations.map(fieldValidation => Object.values(fieldValidation || {}));
    const flattenedKeys = flatten(keys).map(fieldValidation => fieldValidation.key);
    /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    return acc.concat([flattenedKeys]);
  }, []);
  return object(Object.values(TabValidationsKeys || {}), validationKeys);
});
export const isEditorValid = createSelector(getEditorValidation, editorValidation => isEmpty(flatten(Object.values(editorValidation || {}))));
export function getLastPublishState(state) {
  return state.formDefinition.lastPublishState;
}
export function getFormUpdateState(state) {
  return state.formDefinition.updateState;
}
export const isFormSaving = state => getFormUpdateState(state) === UpdateState.SAVING;
export const getFieldValidation = (state, step, field) => getFormDefinitionValidation(state)[step][field];
export const getSubmitLabelValidation = state => {
  const maybeValidation = getFieldValidation(state, TabValidationsKeys.FORM, ValidationKeys.SUBMIT_LABEL);
  return !isEmpty(maybeValidation) ? maybeValidation[ValidationType.REQUIRED] : {};
};
export const getEmailAddressValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.EMAIL_NOTIFICATION_ADDRESS) || {};
export const getThankYouMessageValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.THANK_YOU_MESSAGE) || {};
export const getRedirectPageValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.REDIRECT_PAGE) || {};
export const getFallbackRouteValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.FALLBACK_ROUTE) || {};
export const getConditionalRouteValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.CONDITIONAL_ROUTE) || {};
export const getRoutingFieldValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.ROUTING_FIELD) || {};
export const getRoutingConditionValidation = state => getFieldValidation(state, TabValidationsKeys.OPTIONS, ValidationKeys.ROUTING_CONDITION) || {};
export const getFieldMinValidation = (state, fieldName, objectTypeId) => getFieldValidation(state, TabValidationsKeys.FORM, `fields.${objectTypeId}.${fieldName}.${ValidationKeys.FIELD_MIN_GREATER_THAN_MAX}`);
export const getFieldMaxValidation = (state, fieldName, objectTypeId) => getFieldValidation(state, TabValidationsKeys.FORM, `fields.${objectTypeId}.${fieldName}.${ValidationKeys.FIELD_MAX_LESS_THAN_MIN}`);
export function hasCustomCssEnabled(state) {
  return getMetaDataValue(state, 'css') === '';
}
export function getSfdcCampaignId(state) {
  return getMetaDataValue(state, 'sfdcCampaignId');
}
export function getFormLanguage(state) {
  return getMetaDataValue(state, 'lang');
}
export function getFormLanguageAsHubSpotLanguage(state) {
  const lang = getFormLanguage(state);
  return lang ? getFormsLangToLang(getFormLanguage(state)) : getDefaultLang();
}
export const getSmartFormFields = createSelector(getFormFieldGroups, formFieldGroups => {
  return flatten(formFieldGroups.map(formFieldGroup => formFieldGroup.fields)).filter(formField => formField.isSmartField);
});
export const containsEmailField = createSelector(getFormFieldGroups, formFieldGroups => {
  for (let i = 0; i < formFieldGroups.length; i++) {
    const formFieldGroup = formFieldGroups[i];
    for (let j = 0; j < formFieldGroup.fields.length; j++) {
      const formField = formFieldGroup.fields[j];
      if (isEqual(formField.name, EMAIL_FIELD_NAME)) {
        return true;
      }
      for (let k = 0; k < formField.dependentFieldFilters.length; k++) {
        const dependentField = formField.dependentFieldFilters[k].dependentFormField;
        if (dependentField && isEqual(dependentField.name, EMAIL_FIELD_NAME)) {
          return true;
        }
      }
    }
  }
  return false;
});
export function getIsLegacyThemeAllowed(state) {
  return getMetaDataValue(state, IS_LEGACY_THEME_ALLOWED) === 'true' || getFormThemeName(state) === DefaultThemes.LEGACY;
}
export function getIsRenderRawHtmlEnabled(state) {
  const {
    IS_RENDER_RAW_HTML_ENABLED
  } = MetaDataProperties;
  const isRenderRawHtmlEnabled = getMetaDataValue(state, IS_RENDER_RAW_HTML_ENABLED);
  const isExternalEditor = getIsExternalEditorCOS();
  return isExternalEditor ? true : isRenderRawHtmlEnabled === 'true';
}
export function getIsAllowCookieResetEnabled(state) {
  return getMetaDataValue(state, MetaDataProperties.IS_ALLOW_COOKIE_RESET_ENABLED) === 'true';
}
export function getFormStyle(state, getDefault = false) {
  const formStyle = state.formDefinition.definition.style;
  if (isEmpty(formStyle) || getDefault) {
    const globalStyleSettings = getGlobalFormStyles(state);
    const defaults = Object.values(FormStyleKeys || {}).reduce((memo, key) => {
      const value = globalStyleSettings[key] || globalStyleSettings[FormStyleKeysToGlobalMap[key]];
      return !value ? memo : Object.assign({}, memo, {
        [key]: value
      });
    }, {});
    return createFormStyle(Object.assign({
      [FormStyleKeys.BACKGROUND_WIDTH]: '100%'
    }, defaults));
  }
  return formStyle;
}
export function getFormFieldNeedingProperty(state) {
  return getFormFields(state).filter(({
    name
  }) => isEmpty(name))[0];
}
export function isNewPropertyNeeded(state) {
  return !isUndefined(getFormFieldNeedingProperty(state));
}
export const canCreateMarketableContact = state => getDefinition(state).createMarketableContact;
export const getIsPublished = state => getDefinition(state).isPublished;
export const isTicketForm = state => getFormType(state) === FormTypes.TICKET_FORM;
const getStyleAndPreview = state => state.formDefinition.styleAndPreview;
export const getThemeOpen = state => getStyleAndPreview(state).get('theme');
export const getStyleOpen = state => getStyleAndPreview(state).get('style');
export const getTestOpen = state => getStyleAndPreview(state).get('test');
export const getPaginationOpen = state => getStyleAndPreview(state).get('pagination');
export const hasRequiredTicketFields = createSelector([getAllFieldsByPropertyId], allFieldsByPropertyId => {
  const fieldsToFilter = updateMapKeysForUsingObjectTypes(allFieldsByPropertyId);
  const newObjectTypeFilter = propertyName => fieldsToFilter.has(`${PropertyObjectTypesIds[PropertyObjectTypes.TICKET]}/${propertyName}`);
  return requiredTicketPropertiesForConversations.filter(newObjectTypeFilter).length === requiredTicketPropertiesForConversations.length;
});
export function getEmbedType(state) {
  return getMetaDataValue(state, 'embedType');
}
export const isLegacyTheme = state => getFormThemeName(state) === 'legacy';
const isPhoneField = field => ['mobilephone', 'phone'].indexOf(field.name) > -1;
export const hasPhoneField = state => Boolean(getFormFields(state).find(i => isPhoneField(i)));
export const getFormFieldGroupTypes = createSelector([getFormFields, selectObjectTypeIdToNameMap], (fields, objectTypesMap) => {
  if (objectTypesMap) {
    return uniq(fields.map(field => ({
      objectTypeId: field.objectTypeId,
      label: objectTypesMap[field.objectTypeId]
    })), item => item.objectTypeId);
  }
  return [];
});
export const hasContactsPropertiesInGroups = createSelector(getFormFieldGroupTypes, groupTypes => groupTypes.some(type => isContactProperty(type.objectTypeId)));
export const getIsAutosaveRequired = state => {
  const hasBlankPropertyField = isNewPropertyNeeded(state);
  const hasFormsWriteAccess = hasFormsWriteAccessFromSelector(state);
  const isFormChanged = getFormUpdateState(state).hasUpdated();
  return !hasBlankPropertyField && hasFormsWriteAccess && isEditorValid && isFormChanged;
};
export const getIsPaginatedForm = createSelector([getFormFieldGroups], groups => groups.some(({
  isPageBreak
}) => isPageBreak));
export const getNumberOfPages = createSelector([getFormFieldGroups], groups => groups.filter(({
  isPageBreak
}) => isPageBreak).length + 1);
export const getFormDefinitionFrontendValidationErrorList = state => Object.entries(getFormDefinitionValidation(state)).reduce((acc, [tab, errors]) => {
  if (Object.keys(errors).length) {
    const errorsToShow = Object.entries(errors).map(([location, singleError]) => {
      const [{
        key
      }] = Object.values(singleError);
      return {
        tab,
        location,
        key
      };
    });
    return [...acc, ...errorsToShow];
  }
  return acc;
}, []);
export const getFormDefinitionErrorList = createSelector([getFormDefinitionUpdateErrorList, getFormDefinitionFrontendValidationErrorList], (fieldErrors, frontendValidationErrors) => {
  return {
    fieldErrors,
    frontendValidationErrors
  };
});
export const getFormErrorsDividedByTab = state => {
  const errorList = getFormDefinitionErrorList(state);
  const editorValidation = getEditorValidation(state);
  return Object.assign({}, editorValidation, {
    fieldErrors: errorList.fieldErrors
  });
};
export const getPaymentFieldFromFormDefinition = createSelector(getFormFields, fields => fields.find(field => isFieldAPaymentField(field)));
export const getPublishedPaymentField = state => {
  const {
    formFieldGroups
  } = getPublishedForm(state);
  const flattenedFormFields = flatten((formFieldGroups || []).map(el => el.fields));
  const paymentField = flattenedFormFields.find(field => isFieldAPaymentField(field));
  return paymentField;
};
export const getPaymentField = createSelector(getFormFields, fields => fields.find(field => isFieldAPaymentField(field)));
export const getDisableCookieSubmitionMetaData = state => {
  const metaData = getFormMetaData(state);
  const disableCookieSubmissionKey = findMetaDataKey(metaData, DISABLE_COOKIE_SUBMISSION);
  const disableCookieSubmitionMetaData = disableCookieSubmissionKey ? isEqual(disableCookieSubmissionKey.value, 'true') : false;
  return disableCookieSubmitionMetaData;
};
export const getIsAdConsentWarningOn = createSelector([getFormFields, getDisableCookieSubmitionMetaData], (fields, submissionMetaData) => {
  const AdField = fields.find(field => field.name === PRIVACY_ADS_CONSENT_NAME && field.objectTypeId === contactObjectTypeId);
  const isAlwaysCreateNewContactOn = Boolean(submissionMetaData);
  return Boolean(AdField) && !isAlwaysCreateNewContactOn;
});